// settings-state.ts — render-scheduling state for the settings panel.
//
// Retires the old "every change calls renderAll() and rebuilds all eight
// sections" model. A section now marks ITSELF dirty (`invalidate(i)`) when its
// own state changes; multiple invalidations in one turn coalesce into a single
// microtask-batched flush; and the host rebuilds only the dirty sections
// (reusing cached descriptors for the rest), so an unrelated interaction never
// re-runs another section's side-effectful build (a calendar's async outbox
// fetch, the bookmarklets palette re-subscribe, …).
//
// Cross-cutting host events (theme repaint, auth-state, workspace switch) call
// the host's synchronous full render directly; this module owns only the
// section-scoped, batched path.

let _flush: (() => void) | null = null;
let _scheduled = false;
let _allDirty = false;
const _dirty = new Set<number>();

/** Register the host's flush (the partial reconcile). Called once at wire time. */
export function onReconcile(flush: () => void): void {
  _flush = flush;
}

/**
 * Mark a section dirty (or, with no arg, the whole panel) and schedule a flush.
 * Section handlers call this instead of the old `ctx.renderAll()`.
 */
export function invalidate(section?: number): void {
  if (section === undefined) _allDirty = true;
  else _dirty.add(section);
  if (_scheduled) return;
  _scheduled = true;
  queueMicrotask(() => {
    _scheduled = false;
    _flush?.();
  });
}

/**
 * Read + clear the pending dirty set for one reconcile pass. `all` means a
 * whole-panel rebuild was requested (or the cache is cold); otherwise only the
 * returned section indices need rebuilding.
 */
export function takeDirty(): { all: boolean; sections: Set<number> } {
  const result = { all: _allDirty, sections: new Set(_dirty) };
  _allDirty = false;
  _dirty.clear();
  return result;
}

/** Drop any pending schedule/dirty (the host just did a full synchronous render). */
export function clearPending(): void {
  _allDirty = false;
  _dirty.clear();
}
